import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import images from "../../../utilities/images/images";
import changeCourseChevron from "../../../utilities/images/study_dashboard/change_course_chevron.svg";
import dailyPathEmptyIcon from "../../../utilities/images/study_dashboard/daily_path_empty_icon.svg";
import { resolveWeekDays } from "../utils/dailyPathUtils";
import "./DailyPath.css";

const TABS = [
  { id: "all", label: "All" },
  { id: "classes", label: "Classes" },
  { id: "tests", label: "Tests & Practice" },
  { id: "homework", label: "Homework" },
  { id: "offline", label: "Offline" },
];

const VISIBLE_ITEM_COUNT = 4;

const DailyPathEmptyState = () => (
  <div className="daily-path__empty-state" role="status">
    <img
      src={dailyPathEmptyIcon}
      alt=""
      className="daily-path__empty-icon"
      aria-hidden="true"
    />
    <div className="daily-path__empty-copy">
      <p className="daily-path__empty-title">No schedule for today</p>
      <p className="daily-path__empty-description">
        Your daily schedule will be displayed as per your batch.
      </p>
    </div>
  </div>
);

const DailyPath = ({
  scheduleDays = [],
  scheduleItems = [],
  selectedDayKey = "",
  onDayChange,
  onItemClick,
  isScheduleLoading = false,
}) => {
  const calendarRef = useRef(null);
  const daysViewportRef = useRef(null);
  const [activeTab, setActiveTab] = useState("all");
  const [showAllItems, setShowAllItems] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const weekDays = useMemo(() => resolveWeekDays(scheduleDays), [scheduleDays]);

  const selectedDate = useMemo(() => {
    if (!selectedDayKey) return new Date();
    const parsed = moment(selectedDayKey, "YYYY-MM-DD", true);
    return parsed.isValid() ? parsed.toDate() : new Date();
  }, [selectedDayKey]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDatePicker]);

  useEffect(() => {
    if (!selectedDayKey || !daysViewportRef.current) return;
    const viewport = daysViewportRef.current;
    const activeButton = viewport.querySelector(".daily-path__day.is-active");
    if (!activeButton) return;

    const viewportRect = viewport.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();
    const offset =
      buttonRect.left -
      viewportRect.left -
      (viewport.clientWidth - buttonRect.width) / 2;

    viewport.scrollTo({
      left: viewport.scrollLeft + offset,
      behavior: "smooth",
    });
  }, [selectedDayKey, weekDays.length]);

  const handleDateSelect = (dateKey) => {
    if (!dateKey) return;
    setShowAllItems(false);
    setShowDatePicker(false);
    onDayChange?.(dateKey);
  };

  const filteredItems = useMemo(() => {
    if (activeTab === "all") return scheduleItems;
    return scheduleItems.filter((item) => item.category === activeTab);
  }, [scheduleItems, activeTab]);

  const visibleItems = showAllItems
    ? filteredItems
    : filteredItems.slice(0, VISIBLE_ITEM_COUNT);
  const canViewMore = filteredItems.length > VISIBLE_ITEM_COUNT;
  const isEmptySchedule = !isScheduleLoading && !filteredItems.length;

  return (
    <section className="daily-path" aria-labelledby="daily-path-title">
      <header className="daily-path__header">
        <div className="daily-path__calendar-trigger" ref={calendarRef}>
          <button
            type="button"
            className="daily-path__icon-wrap"
            aria-label="Choose schedule date"
            aria-expanded={showDatePicker}
            onClick={() => setShowDatePicker((current) => !current)}
          >
            <img src={images.calender} alt="" aria-hidden="true" />
          </button>

          {showDatePicker ? (
            <div className="daily-path__datepicker">
              <DatePicker
                selected={selectedDate}
                onChange={(date) => {
                  if (!date) return;
                  handleDateSelect(moment(date).format("YYYY-MM-DD"));
                }}
                inline
                maxDate={new Date()}
              />
            </div>
          ) : null}
        </div>

        <h2 id="daily-path-title" className="daily-path__title">
          Your Daily Path
        </h2>
      </header>

      <div
        className={`daily-path__body${
          isEmptySchedule ? " daily-path__body--empty" : ""
        }`}
      >
        <div className="daily-path__calendar-wrap">
        <div
          ref={daysViewportRef}
          className="daily-path__days"
          role="tablist"
          aria-label="Schedule dates"
        >
          {weekDays.map((day) => (
            <button
              key={day.key}
              type="button"
              role="tab"
              aria-selected={selectedDayKey === day.key}
              className={`daily-path__day ${selectedDayKey === day.key ? "is-active" : ""}`}
              onClick={() => handleDateSelect(day.key)}
            >
              <span className="daily-path__day-label">{day.label}</span>
              <strong className="daily-path__day-date">{day.date}</strong>
            </button>
          ))}
        </div>

        <div className="daily-path__tabs" role="tablist" aria-label="Schedule filters">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`daily-path__tab ${activeTab === tab.id ? "is-active" : ""}`}
              onClick={() => {
                setShowAllItems(false);
                setActiveTab(tab.id);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="daily-path__list">
        {isScheduleLoading ? (
          <p className="daily-path__empty">Loading schedule...</p>
        ) : filteredItems.length ? (
          visibleItems.map((item) => {
            const isDisabled = item.isActionable === false;

            return (
              <article
                key={item.id}
                className={`daily-path__item${isDisabled ? " daily-path__item--disabled" : ""}`}
              >
                <div className="daily-path__item-body">
                  {item.badge ? (
                    <span
                      className={`daily-path__badge daily-path__badge--${item.badgeType || "default"}`}
                    >
                      <span className="daily-path__badge-dot" aria-hidden="true" />
                      {item.badge}
                    </span>
                  ) : null}

                  <div className="daily-path__item-content">
                    <p className="daily-path__item-title">{item.title}</p>
                    {item.subtitle ? (
                      <p className="daily-path__item-subtitle">{item.subtitle}</p>
                    ) : null}
                  </div>

                  {item.instructor ? (
                    <div className="daily-path__instructor">
                      {item.instructorAvatar ? (
                        <img
                          src={item.instructorAvatar}
                          alt=""
                          className="daily-path__instructor-avatar"
                          aria-hidden="true"
                        />
                      ) : (
                        <span className="daily-path__instructor-avatar daily-path__instructor-avatar--fallback" />
                      )}
                      <span>{item.instructor}</span>
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="daily-path__action"
                  aria-label={`Open ${item.title}`}
                  disabled={isDisabled}
                  onClick={() => onItemClick?.(item)}
                >
                  <img src={changeCourseChevron} alt="" aria-hidden="true" />
                </button>
              </article>
            );
          })
        ) : (
          <DailyPathEmptyState />
        )}
      </div>
      </div>

      {canViewMore ? (
        <button
          type="button"
          className="daily-path__view-all"
          onClick={() => setShowAllItems((current) => !current)}
        >
          {showAllItems ? "Show Less" : "View All"}
          <img
            src={changeCourseChevron}
            alt=""
            aria-hidden="true"
            className={showAllItems ? "is-rotated" : ""}
          />
        </button>
      ) : null}
    </section>
  );
};

export default DailyPath;
